/* eslint-disable max-len */
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai/utils';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import { useEffect, useState } from 'react';
import { search_filter_atom } from '../store/searchSheet';
import { getSearchFilter } from 'api2';
import {
  GetCategoryProductListVariables,
  UxGoodsColorFilterOptionList as ColorFilter,
  UxGoodsPriceRangeSelectFilterOptionList as PriceFilter,
  UxGoodsCategoryFilterOptionList as CategoryFilter,
  UxGoodsDeliveryFilterOptionList as DeliveryFilter,
  SearchedGoodsPriceRange,
  UxGoodsFilterOptionType,
} from 'api2/types';
import { getFilterIdList, SearchFilter } from 'util/search_utils';

type Category = CategoryFilter['category_option_list'][number];
type Color = ColorFilter['color_option_list'][number];
type Price = PriceFilter['price_range_list'][number];
type Delivery = DeliveryFilter['delivery_option_list'][number];

export type SearchListType = ColorFilter | PriceFilter | DeliveryFilter;

export const SEARCH_FILTER_QUERY_KEY = 'getSearchFilter';

export const fetchSearchFilter = async (search_filter: SearchFilter) => {
  const price_range: SearchedGoodsPriceRange | null = search_filter.price
    ? {
        gte: search_filter.price.min,
        lte: search_filter.price.max,
      }
    : null;
  const req: GetCategoryProductListVariables = omitBy(
    {
      filter_id_list: getFilterIdList(search_filter),
      goods_filter_option: omitBy(
        {
          color_list: search_filter.color_list,
          display_category_id_list: search_filter.category_id_list,
          price_range,
          title: search_filter.keyword,
        },
        isNil,
      ),
    },
    isNil,
  );
  const res = await getSearchFilter(req, { show_alert: false });
  return res.data.fbk_srp_search_product_list?.goods_filter_option_list?.item_list ?? [];
};

/**
 * 검색필터에 필요한 데이터 쿼리 조회
 * @hook
 */
const useSearchFilter = () => {
  const [is_first_load_success, setIsFirstLoadSuccess] = useState(false);
  const search_filter = useAtomValue(search_filter_atom);

  const {
    data = [],
    isFetching: is_loading,
    ...rest
  } = useQuery([SEARCH_FILTER_QUERY_KEY, search_filter], () => fetchSearchFilter(search_filter), {
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data.length > 0 && !is_first_load_success) {
      setIsFirstLoadSuccess(true);
    }
  }, [data, is_first_load_success]);

  const search_options = getSearchFilterOptions(data as SearchListType[]);
  return { ...search_options, ...rest, is_loading, is_first_load_success };
};

export default useSearchFilter;

function getSearchFilterOptions(search_list: SearchListType[]) {
  let color_list: Color[] = [];
  let price_list: Price[] = [];
  let category_list: Category[] = [];
  let delivery_list: Delivery[] = [];
  let total_count = 0;

  search_list.forEach((item) => {
    switch (item.type) {
      case UxGoodsFilterOptionType.UX_GOODS_COLOR_FILTER_OPTION_LIST: {
        color_list = [...(item as ColorFilter).color_option_list];
        return;
      }
      case UxGoodsFilterOptionType.UX_GOODS_PRICE_RANGE_FILTER_OPTION_LIST: {
        total_count = (item as PriceFilter).total_count;
        price_list = [...(item as PriceFilter).price_range_list];
        return;
      }
      case UxGoodsFilterOptionType.UX_GOODS_CATEGORY_FILTER_OPTION_LIST: {
        category_list = [...(item as unknown as CategoryFilter).category_option_list];
        return;
      }
      case UxGoodsFilterOptionType.UX_GOODS_DELIVERY_FILTER_OPTION_LIST: {
        delivery_list = [...(item as DeliveryFilter).delivery_option_list];
        return;
      }
    }
  });

  return {
    color_list,
    price_list,
    category_list,
    delivery_list,
    total_count,
  };
}
